import { configService } from './configService';
import { authService } from './authService';

// アップロードサービス
export const uploadService = {
  /**
   * アップロード用の署名付きURLを取得する
   * @param companyId 企業ID
   * @param s3Key S3キー
   * @param fileName ファイル名
   * @param contentType コンテンツタイプ
   * @param scenarioId シナリオID
   * @returns 署名付きURL
   */
  async getUploadUrl(companyId: string, s3Key: string, fileName: string, contentType: string, scenarioId: string): Promise<string> {
    try {
      const config = await configService.loadConfig();
      
      // IDトークンを取得
      const idToken = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
      if (!idToken) {
        throw new Error('認証情報が見つかりません。再度ログインしてください。');
      }
      
      // APIのURLを構築
      const baseUrl = config.apiUrl.endsWith('/') ? config.apiUrl.slice(0, -1) : config.apiUrl;
      const response = await fetch(`${baseUrl}/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          companyId,
          s3Key,
          fileName,
          contentType,
          scenarioId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '署名付きURLの取得に失敗しました');
      }
      
      const data = await response.json();
      return data.presignedUrl;
    } catch (error) {
      console.error('署名付きURLの取得中にエラーが発生しました:', error);
      
      // トークンの有効期限が切れている場合はトークンを更新してリトライ
      if (authService.isTokenExpired()) {
        const refreshResult = await authService.refreshToken();
        if (refreshResult.success) {
          // トークンの更新に成功したらリトライ
          return this.getUploadUrl(companyId, s3Key, fileName, contentType, scenarioId);
        }
      }
      
      throw error;
    }
  },
  
  /**
   * 署名付きURLを使用してファイルをアップロードする
   * @param presignedUrl 署名付きURL
   * @param file アップロードするファイル
   * @returns アップロード結果
   */
  async uploadFileWithPresignedUrl(presignedUrl: string, file: File): Promise<void> {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });
      
      if (!response.ok) {
        throw new Error('ファイルのアップロードに失敗しました');
      }
    } catch (error) {
      console.error('ファイルアップロード中にエラーが発生しました:', error);
      throw error;
    }
  },

  /**
   * 教材閲覧用の署名付きURLを取得する
   * @param materialId 教材ID
   * @returns 署名付きURL、ファイル名、コンテンツタイプ
   */
  async getMaterialUrl(materialId: string): Promise<{ presignedUrl: string; fileName: string; contentType: string }> {
    try {
      const config = await configService.loadConfig();
      
      // IDトークンを取得
      const idToken = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
      if (!idToken) {
        throw new Error('認証情報が見つかりません。再度ログインしてください。');
      }
      
      // APIのURLを構築
      const baseUrl = config.apiUrl.endsWith('/') ? config.apiUrl.slice(0, -1) : config.apiUrl;
      const response = await fetch(`${baseUrl}/get-material-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          materialId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '署名付きURLの取得に失敗しました');
      }
      
      const data = await response.json();
      return {
        presignedUrl: data.presignedUrl,
        fileName: data.fileName,
        contentType: data.contentType
      };
    } catch (error) {
      console.error('署名付きURLの取得中にエラーが発生しました:', error);
      
      // トークンの有効期限が切れている場合はトークンを更新してリトライ
      if (authService.isTokenExpired()) {
        const refreshResult = await authService.refreshToken();
        if (refreshResult.success) {
          // トークンの更新に成功したらリトライ
          return this.getMaterialUrl(materialId);
        }
      }
      
      throw error;
    }
  },

  /**
   * 評価結果PDF用の署名付きURLを取得する
   * @param materialId 教材ID
   * @returns 署名付きURL、ファイル名、コンテンツタイプ
   */
  async getEvaluationUrl(evaluationId: string): Promise<{ presignedUrl: string; fileName: string; contentType: string }> {
    try {
      const config = await configService.loadConfig();
      
      // IDトークンを取得
      const idToken = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
      if (!idToken) {
        throw new Error('認証情報が見つかりません。再度ログインしてください。');
      }
      
      // APIのURLを構築
      const baseUrl = config.apiUrl.endsWith('/') ? config.apiUrl.slice(0, -1) : config.apiUrl;
      const response = await fetch(`${baseUrl}/get-evaluation-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          evaluationId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '署名付きURLの取得に失敗しました');
      }
      
      const data = await response.json();
      return {
        presignedUrl: data.presignedUrl,
        fileName: data.fileName,
        contentType: data.contentType
      };
    } catch (error) {
      console.error('署名付きURLの取得中にエラーが発生しました:', error);
      
      // トークンの有効期限が切れている場合はトークンを更新してリトライ
      if (authService.isTokenExpired()) {
        const refreshResult = await authService.refreshToken();
        if (refreshResult.success) {
          // トークンの更新に成功したらリトライ
          return this.getEvaluationUrl(evaluationId);
        }
      }
      
      throw error;
    }
  },

  /**
   * CSVダウンロード用の署名付きURLを取得する
   * @param requestId CSVダウンロードリクエストID
   */
  async getCsvUrl(requestId: string): Promise<{ presignedUrl: string; fileName: string; contentType: string }> {
    try {
      const config = await configService.loadConfig();

      // IDトークンを取得
      const idToken = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
      if (!idToken) {
        throw new Error('認証情報が見つかりません。再度ログインしてください。');
      }

      // APIのURLを構築
      const baseUrl = config.apiUrl.endsWith('/') ? config.apiUrl.slice(0, -1) : config.apiUrl;
      const response = await fetch(`${baseUrl}/get-csv-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          requestId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '署名付きURLの取得に失敗しました');
      }

      const data = await response.json();
      return {
        presignedUrl: data.presignedUrl,
        fileName: data.fileName,
        contentType: data.contentType
      };
    } catch (error) {
      console.error('署名付きURLの取得中にエラーが発生しました:', error);

      // トークンの有効期限が切れている場合はトークンを更新してリトライ
      if (authService.isTokenExpired()) {
        const refreshResult = await authService.refreshToken();
        if (refreshResult.success) {
          // トークンの更新に成功したらリトライ
          return this.getCsvUrl(requestId);
        }
      }

      throw error;
    }
  }
};
